import React, { useState, useEffect, useMemo } from "react";
import type { Tenant, Role } from "@/types";
import { useTenants } from "@/store/tenant";
import { useRoles } from "@/store/role";
import { useApplications } from "@/store/application";
import { Button } from "@/components/ui/Button";
import {
  ShieldCheck,
  Save,
  Search,
  UserX,
  Building2,
  AppWindow,
  Layers,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

interface TenantOnboardingTabProps {
  tenant: Tenant;
  onUpdate?: (updated: Tenant) => void;
}

export const TenantOnboardingTab: React.FC<TenantOnboardingTabProps> = ({
  tenant,
  onUpdate,
}) => {
  const [autoApprove, setAutoApprove] = useState<boolean>(
    tenant.auto_approve ?? false
  );
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>(
    tenant.auto_approve_role_ids ?? []
  );
  const [roleSearch, setRoleSearch] = useState<string>("");
  const [scopeFilter, setScopeFilter] = useState<"all" | "tenant" | "app">("all");
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const listRoles = useRoles((state) => state.list);
  const roles = useRoles((state) => state.roles);
  const listApps = useApplications((state) => state.list);
  const apps = useApplications((state) => state.applications);

  useEffect(() => {
    Promise.all([listRoles(), listApps()]).catch(console.error);
  }, [listRoles, listApps]);

  useEffect(() => {
    setAutoApprove(tenant.auto_approve ?? false);
    setSelectedRoleIds(tenant.auto_approve_role_ids ?? []);
  }, [tenant]);

  const isAppRole = (role: Role): boolean => {
    const scopes = role.applicable_scopes || [];
    const category = (role.category || "").toLowerCase();
    const kind = (role.resource_kind_id || "").toLowerCase();
    const idLower = role.id.toLowerCase();
    return (
      scopes.includes("TenantApplication") ||
      scopes.includes("Application") ||
      category.includes("app") ||
      kind.includes("application") ||
      kind.includes("app") ||
      idLower.startsWith("platform::application")
    );
  };

  const getMatchingApp = (role: Role) => {
    const kind = (role.resource_kind_id || "").toLowerCase();
    const idLower = role.id.toLowerCase();
    return apps.find(
      (a) =>
        kind.includes(a.name.toLowerCase()) ||
        kind.includes(a.id.toLowerCase()) ||
        idLower.includes(a.name.toLowerCase()) ||
        idLower.includes(a.id.toLowerCase())
    ) || null;
  };

  const { tenantRoles, appRoles } = useMemo(() => {
    const tRoles: Role[] = [];
    const aRoles: Role[] = [];
    roles.forEach((r) => {
      if (isAppRole(r)) aRoles.push(r);
      else tRoles.push(r);
    });
    return { tenantRoles: tRoles, appRoles: aRoles };
  }, [roles, apps]);

  const matchesSearch = (r: Role): boolean => {
    if (!roleSearch.trim()) return true;
    const q = roleSearch.toLowerCase();
    const app = getMatchingApp(r);
    return (
      r.name.toLowerCase().includes(q) ||
      (r.description?.toLowerCase().includes(q) ?? false) ||
      r.id.toLowerCase().includes(q) ||
      (app?.name.toLowerCase().includes(q) ?? false)
    );
  };

  const filteredTenantRoles = useMemo(
    () => tenantRoles.filter(matchesSearch),
    [tenantRoles, roleSearch, apps]
  );

  const filteredAppRolesGrouped = useMemo(() => {
    const filtered = appRoles.filter(matchesSearch);
    const groupsMap: Record<string, { appName: string; logoUrl?: string; roles: Role[] }> = {};
    filtered.forEach((role) => {
      const app = getMatchingApp(role);
      const key = app?.name ?? "General Application Roles";
      if (!groupsMap[key]) {
        groupsMap[key] = { appName: key, logoUrl: app?.logo_url, roles: [] };
      }
      groupsMap[key].roles.push(role);
    });
    return Object.values(groupsMap);
  }, [appRoles, roleSearch, apps]);

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await useTenants.getState().update(tenant.id, {
        auto_approve: autoApprove,
        auto_approve_role_ids: selectedRoleIds,
      });
      onUpdate?.(updated);
      toast.success("Onboarding settings saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save onboarding settings");
    } finally {
      setIsSaving(false);
    }
  };

  const selectedTenantCount = selectedRoleIds.filter((id) => tenantRoles.some((r) => r.id === id)).length;
  const selectedAppCount = selectedRoleIds.filter((id) => appRoles.some((r) => r.id === id)).length;

  return (
    <div className="space-y-6">

      {/* AutoApprove Policy */}
      <div className="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-sm border border-slate-200 dark:border-slate-800">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
          AutoApprove Policy
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
          Choose whether user access requests are auto-approved by the <span className="font-medium text-slate-700 dark:text-slate-300">Platform</span> actor or require manual admin review.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Manual */}
          <label
            className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
              !autoApprove
                ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
            }`}
          >
            <input
              type="radio"
              name="autoApproveRadio"
              checked={!autoApprove}
              onChange={() => setAutoApprove(false)}
              className="mt-0.5 w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
            />
            <div>
              <div className="flex items-center gap-2">
                <UserX className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-slate-900 dark:text-white">Manual Review</span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Requests stay <strong>PENDING</strong> until an admin approves or rejects.
              </p>
            </div>
          </label>

          {/* AutoApprove */}
          <label
            className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
              autoApprove
                ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
            }`}
          >
            <input
              type="radio"
              name="autoApproveRadio"
              checked={autoApprove}
              onChange={() => setAutoApprove(true)}
              className="mt-0.5 w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
            />
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium text-slate-900 dark:text-white">AutoApprove</span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Requests are immediately set to <strong>ACTIVE</strong>. Actor logged as <strong>Platform</strong>.
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Auto-Assigned Roles — only visible when AutoApprove is enabled */}
      {autoApprove && (
      <div className="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-sm border border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Auto-Assigned Roles
          </h3>
          {(selectedTenantCount + selectedAppCount) > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 px-2 py-0.5 rounded-full">
              <CheckCircle2 className="w-3 h-3" />
              {selectedTenantCount + selectedAppCount} selected
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
          Roles automatically granted when a user is approved during onboarding.
        </p>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
          {/* Scope Filter */}
          <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden text-xs">
            {(["all", "tenant", "app"] as const).map((f) => {
              const labels: Record<typeof f, string> = {
                all: `All (${roles.length})`,
                tenant: `Tenant (${tenantRoles.length})`,
                app: `Application (${appRoles.length})`,
              };
              const icons: Record<typeof f, React.ReactNode> = {
                all: <Layers className="w-3 h-3" />,
                tenant: <Building2 className="w-3 h-3" />,
                app: <AppWindow className="w-3 h-3" />,
              };
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setScopeFilter(f)}
                  className={`flex items-center gap-1 px-3 py-1.5 font-medium transition-colors border-r border-slate-200 dark:border-slate-700 last:border-r-0 ${
                    scopeFilter === f
                      ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900"
                      : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                  }`}
                >
                  {icons[f]}
                  {labels[f]}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-56">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-slate-400" />
            <input
              type="text"
              value={roleSearch}
              onChange={(e) => setRoleSearch(e.target.value)}
              placeholder="Search roles..."
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* SECTION 1 — Tenant Level Roles */}
        {(scopeFilter === "all" || scopeFilter === "tenant") && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Tenant Level Roles
              </span>
              <span className="ml-auto text-xs text-slate-400">
                {selectedTenantCount} of {tenantRoles.length} selected
              </span>
            </div>

            {filteredTenantRoles.length === 0 ? (
              <p className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
                No tenant-level roles found.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {filteredTenantRoles.map((role) => {
                  const isSelected = selectedRoleIds.includes(role.id);
                  return (
                    <div
                      key={role.id}
                      onClick={() => toggleRole(role.id)}
                      className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                          : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRole(role.id)}
                        className="mt-0.5 w-3.5 h-3.5 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-xs font-medium text-slate-900 dark:text-white truncate">
                            {role.name}
                          </span>
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 flex-shrink-0">
                            Tenant
                          </span>
                        </div>
                        {role.description && (
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">
                            {role.description}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* SECTION 2 — Application Level Roles (grouped by app) */}
        {(scopeFilter === "all" || scopeFilter === "app") && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <AppWindow className="w-3.5 h-3.5 text-purple-500" />
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Application Level Roles
              </span>
              <span className="ml-auto text-xs text-slate-400">
                {selectedAppCount} of {appRoles.length} selected
              </span>
            </div>

            {filteredAppRolesGrouped.length === 0 ? (
              <p className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
                No application-level roles found.
              </p>
            ) : (
              <div className="space-y-4">
                {filteredAppRolesGrouped.map((appGroup) => {
                  const groupSelected = appGroup.roles.filter((r) => selectedRoleIds.includes(r.id)).length;
                  return (
                    <div key={appGroup.appName} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                      {/* App group header */}
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                        {appGroup.logoUrl ? (
                          <img
                            src={appGroup.logoUrl}
                            alt={appGroup.appName}
                            className="w-4 h-4 object-contain rounded"
                          />
                        ) : (
                          <AppWindow className="w-3.5 h-3.5 text-purple-500" />
                        )}
                        <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                          {appGroup.appName}
                        </span>
                        <span className="ml-auto text-[11px] text-slate-400">
                          {groupSelected} of {appGroup.roles.length} selected
                        </span>
                      </div>

                      {/* Roles for this app */}
                      <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {appGroup.roles.map((role) => {
                          const isSelected = selectedRoleIds.includes(role.id);
                          return (
                            <div
                              key={role.id}
                              onClick={() => toggleRole(role.id)}
                              className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${
                                isSelected
                                  ? "border-purple-500 bg-purple-50 dark:bg-purple-950/20"
                                  : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleRole(role.id)}
                                className="mt-0.5 w-3.5 h-3.5 text-purple-600 rounded border-slate-300 focus:ring-purple-500"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-1">
                                  <span className="text-xs font-medium text-slate-900 dark:text-white truncate">
                                    {role.name}
                                  </span>
                                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 flex-shrink-0">
                                    App
                                  </span>
                                </div>
                                {role.description && (
                                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">
                                    {role.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving} className="px-6">
          {isSaving ? (
            "Saving..."
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Onboarding Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
